import { useMemo, useState } from "react";
import { PaginationProps, Flex, message, Modal } from "antd";
import { ExpenseType } from "@/types/expenseType";
import { DEFAULT_PAGINATION } from "@/constants/table";
import {
  useGetExpenses,
  useGetExpensesOptions,
} from "@/pages/Reports/hooks/useReports";
import { getColumns } from "./utils/table";
import { ExpenseDescription } from "@/types/expenseDescription";
import { useLocation, useNavigate } from "react-router-dom";
import Title from "antd/es/typography/Title";
import { ArrowLeftOutlined } from "@ant-design/icons";
import useModal from "@/hooks/useModal";
import { Expense } from "@/types/expense";
import { useDeleteExpense } from "@/pages/Expenses/Overview/hooks/useExpense";
import ExpenseEditModal from "@/pages/Expenses/Overview/components/ExpenseEditModal";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";
import { FilterCondition } from "@/components/table/hooks/useFilter";

export interface ExpenseGroupDetailData {
  expenseType?: ExpenseType;
  description?: ExpenseDescription;
  startDate?: string;
  endDate?: string;
}

const ExpenseGroupDetail = () => {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortCondition | undefined>({
    field: "price",
    order: "desc",
  });
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>();
  const stateData = useLocation().state as ExpenseGroupDetailData;
  const { expenseType, description, startDate, endDate } = stateData || {};

  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<Expense>();

  const { mutate: deleteExpense } = useDeleteExpense();
  const handleDelete = (expense: Expense) => {
    Modal.confirm({
      title: "Remove this expense?",
      content: "This action cannot be undone.",
      okText: "Remove Expense",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        deleteExpense(expense.id, {
          onSuccess: () => {
            message.success("Successfully removed expense");
            refetch();
          },
        });
      },
    });
  };

  // Get expenses by description
  const {
    data: expensesData,
    isFetching,
    refetch,
  } = useGetExpenses({
    config: {
      params: {
        expense_type_id_eq: expenseType?.id,
        description_id_eq: description?.id,
        date_between:
          startDate && endDate ? `${startDate},${endDate}` : undefined,
        filters: filters.length > 0 ? filters : undefined,
        sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
        include_inactive: false,
        limit: pagination?.pageSize,
        page: pagination?.current,
        offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 5),
      },
    },
    enabled: !!description?.id && !!expenseType?.id,
  });

  // Get filter options (unit and supplier)
  const { data: options } = useGetExpensesOptions({
    config: {
      params: {
        only: "unit.id,unit.name,supplier.id,supplier.name",
        expense_type_id_eq: expenseType?.id,
        description_id_eq: description?.id,
        date_between:
          startDate && endDate ? `${startDate},${endDate}` : undefined,
        include_inactive: false,
        limit: -1,
      },
    },
    enabled: !!description?.id && !!expenseType?.id,
  });

  const filterOptions = useMemo(
    () => ({
      unit_id: (options || []).reduce((acc, item) => {
        if (
          item.unit &&
          !acc.some((option) => option.value === item?.unit?.id)
        ) {
          acc.push({
            text: item.unit.name,
            value: item.unit.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      supplier_id: (options || []).reduce((acc, item) => {
        if (
          item.supplier &&
          !acc.some((option) => option.value === item?.supplier?.id)
        ) {
          acc.push({
            text: item.supplier.name,
            value: item.supplier.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  // Redirect to reports if expense type or description is not found
  if (!expenseType || !description) {
    navigate("/overview");
  }

  return (
    <>
      <Flex
        vertical={false}
        gap={16}
        style={{
          marginBottom: 24,
          alignContent: "center",
          alignItems: "center",
        }}
      >
        <ArrowLeftOutlined
          style={{
            cursor: "pointer",
            color: "#000000",
            fontSize: 18,
          }}
          onClick={() => navigate(-1)}
        />
        <Title level={2} style={{ margin: 0 }}>
          {description?.name}
        </Title>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            onEdit: openEditModal,
            onDelete: handleDelete,
            filterOptions,
            expenseType,
          })
        }
        onRow={() => ({
          style: {
            verticalAlign: "top",
          },
        })}
        dataSource={expensesData?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            pageSize: 50,
            showSizeChanger: true,
          },
          dataPagination: expensesData?.pagination,
          onChange: setPagination,
        }}
        scroll={{ x: "max-content" }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />

      <ExpenseEditModal
        isOpen={isEditModalOpen}
        data={editModalData}
        onClose={closeEditModal}
        onRefetch={refetch}
      />
    </>
  );
};

export default ExpenseGroupDetail;
